import React, { useState, useEffect } from "react";
import "../css/usermanagement.css";
import { getWithTokenAPI, putAPI, postWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Typography,
  Button,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import GroupIcon from "@mui/icons-material/Group";
import { useTheme } from "@mui/material/styles"; 

function UserManagement() {
  const [userList, setUserList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10); 

  const [newUserData, setNewUserData] = useState({
    name: "",
    email: "",
    phone_number: "",
    password: "",
    role: "user",
  });

  const theme = useTheme(); 
  const secondaryTextColor = theme.palette.text.secondary; 

  const fetchUsers = async (filterType = null, filterValue = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      let url = "/user/find-all?role=user";
      const queryParams = [];

      if (filterType && filterValue) {
        queryParams.push(`${filterType}=${filterValue}`);
      }

      if (queryParams.length > 0) {
        url += `&${queryParams.join("&")}`;
      }

      const res = await getWithTokenAPI(token, url);
      const users = res?.data?.User || [];
      setUserList(users);
      setFilteredUsers(users);
    } catch (error) {
      toast.error("Failed to fetch users");
      console.error("Fetch error:", error);
      setUserList([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastOrder = currentPage * usersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    let filterType = null;
    let filterValue = null;

    switch (tabId) {
      case "active":
        filterType = "status";
        filterValue = "Active";
        break;
      case "inactive":
        filterType = "status";
        filterValue = "Inactive";
        break;
      default:
        break;
    }
    await fetchUsers(filterType, filterValue);
  };

  useEffect(() => {
    handleTabChange("active");
  }, []);

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    const token = localStorage.getItem("token");

    try {
      setLoading(true);
      const res = await putAPI(token, `/user/update`, {
        _id: userId,
        status: newStatus,
      });

      if (res.statusCode !== 200) {
        throw new Error(res.message || "Failed to update status");
      }

      toast.success("Status updated successfully", {
        autoClose: 1500,
        onClose: () => handleTabChange(activeTab),
      });
    } catch (error) {
      console.error("Toggle status error:", error);
      toast.error(error.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleNewUserInputChange = (e) => {
    const { name, value } = e.target;
    setNewUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddUser = async () => {
    if (
      !newUserData.name ||
      !newUserData.email ||
      !newUserData.phone_number ||
      !newUserData.password
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);
      // Get the token from localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found. Please log in.");
        setLoading(false);
        return;
      }

      // Use postWithTokenAPI instead of postWithoutTokenAPI
      const res = await postWithTokenAPI(token, "/seller/create", newUserData);

      if (res.statusCode === 200) {
        toast.success("User added successfully!", {
          autoClose: 1500,
          onClose: () => {
            setShowAddUserModal(false);
            setNewUserData({
              name: "",
              email: "",
              phone_number: "",
              password: "",
              role: "user",
            });
            handleTabChange(activeTab);
          },
        });
      } else {
        throw new Error(res.message || "Failed to add user.");
      }
    } catch (error) {
      console.error("Add User error:", error);
      toast.error(error.message || "Failed to add user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />

      {/* Header and Add User Button (Search Bar removed) */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <GroupIcon sx={{ color: primaryGreen, fontSize: 50, mr: 2 }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            User
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage Users
          </Typography>
        </Box>
      </Box>

      {/* Tab Navigation */}
      <Box
        sx={{
          backgroundColor: lightGreen,
          p: 0.5,
          borderRadius: 2,
          display: "inline-flex",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            borderBottom: "none",
            "& button": {
              backgroundColor: "white",
              color: primaryGreen,
              border: "1px solid white",
              mr: 0.5,
              fontWeight: "bold",
              borderRadius: "4px",
              minWidth: "120px",
              textTransform: "none",

              "&:hover": {
                backgroundColor: "#DC3545",
                color: "white",
              },
            },
            "& button:last-of-type": {
              mr: 0,
            },
            "& button.active-tab": {
              backgroundColor: primaryGreen,
              color: "white",
              border: `1px solid ${primaryGreen}`,
              "&:hover": {
                backgroundColor: "#DC3545",
                color: "white",
              },
            },
          }}
        >
          {[
            { id: "active", label: "Active" },
            { id: "inactive", label: "Inactive" },
          ].map((tab) => (
            <Button
              key={tab.id}
              className={activeTab === tab.id ? "active-tab" : ""}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* User Table */}
      <div className="table-container border rounded">
        <div className="table-responsive">
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress style={{ color: "#DC3545" }} />
              <Typography sx={{ ml: 2, color: "#DC3545" }}>Loading users...</Typography>
            </Box>
          ) : (
            <table className="table text-center m-0 resource-head">
              <thead
                className="sticky-header fs-6"
                style={{ backgroundColor: lightGreen }}
              >
                <tr>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    S.No
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor , textAlign: 'left'}}
                  >
                    User Name
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor , textAlign: 'left'}}
                  >
                    Email
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor , textAlign: 'left'}}
                  >
                    Phone Number
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor , textAlign: 'left'}}
                  >
                    Status
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor , textAlign: 'left'}}
                  >
                    Verified
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor , textAlign: 'left'}}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  currentUsers.map((user, index) => (
                    <tr key={user._id} >
                      <td style={{textAlign: 'left'}}>{indexOfFirstOrder + index + 1}</td>
                      <td
                        onClick={() =>
                          navigate(`/backoffice/reviewuser/${user._id}`)
                        }
                        style={{
                          fontWeight: "bold",
                          color: primaryGreen,
                          cursor: "pointer",
                          textDecoration: "none",
                          textAlign:'left'
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.textDecoration = "none")
                        }
                      >
                        {user.name}
                      </td>
                      <td style={{textAlign: 'left'}}>{user.email}</td>
                      <td style={{textAlign: 'left'}}>{user.phone_number}</td>
                      <td style={{textAlign: 'left'}}>
                        <div className="form-check form-switch d-flex justify-content-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={user.status === "Active"}
                            onChange={() =>
                              handleStatusToggle(user._id, user.status)
                            }
                            style={{
                              width: "3em",
                              height: "1.5em",
                              backgroundColor:
                                user.status === "Active"
                                  ? "#DC3545"
                                  : "transparent",
                              border:
                                user.status !== "Active"
                                  ? "2px solid #DC3545"
                                  : "none",
                              boxShadow: "none",
                            }}
                          />
                        </div>
                      </td>
                      <td style={{textAlign: 'left'}}>{user.otp_verify ? "Yes" : "No"}</td>
                      <td style={{textAlign: 'left'}}>
                        <IconButton
                          color="info"
                          onClick={() =>
                            navigate(`/backoffice/reviewuser/${user._id}`)
                          }
                          title="View Details"
                          sx={{ color: primaryGreen }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      <GroupIcon
                        sx={{ fontSize: 60, color: "text.secondary" }}
                      />
                      <Typography variant="h6" sx={{ color: "text.secondary" }}>
                        No {activeTab} users found.
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      {filteredUsers.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            mt: 3,
            pb: 2, // Added bottom padding
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="green"
            sx={{
              "& .MuiPaginationItem-root": {
                color: primaryGreen,
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: primaryGreen,
                color: "white",
              },
            }}
          />
        </Box>
      )}

      {/* Add User Modal (Material-UI Dialog) */}
      <Dialog
        open={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
      >
        <DialogTitle sx={{ backgroundColor: primaryGreen, color: "white" }}>
          Add New User
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="User Name"
            name="name"
            value={newUserData.name}
            onChange={handleNewUserInputChange}
            fullWidth
            margin="normal"
            variant="outlined"
            required
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={newUserData.email}
            onChange={handleNewUserInputChange}
            fullWidth
            margin="normal"
            variant="outlined"
            required
          />
          <TextField
            label="Phone Number"
            name="phone_number"
            type="tel"
            value={newUserData.phone_number}
            onChange={handleNewUserInputChange}
            fullWidth
            margin="normal"
            variant="outlined"
            required
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            value={newUserData.password}
            onChange={handleNewUserInputChange}
            fullWidth
            margin="normal"
            variant="outlined"
            required
          />
          <TextField
            label="Role"
            value={newUserData.role}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            helperText="Role is automatically set to 'user'"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAddUserModal(false)}
            style={{ color: "#00b207" }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            sx={{
              backgroundColor: primaryGreen,
              color: "white",
              "&:hover": { backgroundColor: "#b02a37" },
            }}
            disabled={loading}
          >
            {loading ? "Adding..." : "Add User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;
